import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { X, Filter, SortAsc, Bell, CheckCircle, Circle, AlertCircle, Users, User, FilePlus2 } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { api } from '../../lib/api';
import StaffApprovalModal from '../admin/StaffApprovalModal';
import { showAlert } from '../common/AppDialogs';

interface NotificationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedNotificationId?: string | null;
}

interface Notification {
  id: string;
  type: string;
  title: string;
  description: string;
  organizationId: string;
  assignedToType: 'user' | 'group';
  assignedToId: string;
  customData?: Record<string, any>;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
  userStatus?: {
    id: string;
    status: 'unread' | 'read' | 'completed' | 'cancelled';
    readAt?: string;
    updatedAt: string;
  };
  canDelete?: boolean;
}

export default function NotificationsModal({ isOpen, onClose, selectedNotificationId }: NotificationsModalProps) {
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);
  const [filters, setFilters] = useState({
    type: '',
    status: '',
    sortBy: 'date' as 'date' | 'type',
    sortOrder: 'desc' as 'asc' | 'desc',
  });
  const [readTimer, setReadTimer] = useState<NodeJS.Timeout | null>(null);
  const [showStaffApprovalModal, setShowStaffApprovalModal] = useState(false);
  const [currentJoinRequest, setCurrentJoinRequest] = useState<any>(null);

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Fetch notifications
  const { data: notifications = [], isLoading } = useQuery({
    queryKey: ['notifications', filters],
    queryFn: () => {
      const params = new URLSearchParams();
      if (filters.type) params.append('type', filters.type);
      if (filters.status) params.append('status', filters.status);
      params.append('sortBy', filters.sortBy);
      params.append('sortOrder', filters.sortOrder);
      
      return api.get(`/notifications?${params.toString()}`).then(res => res.data.data);
    },
    enabled: isOpen,
    refetchOnWindowFocus: false,
  });

  // Fetch notification types for filtering
  const { data: notificationTypes = [] } = useQuery({
    queryKey: ['notification-types'],
    queryFn: () => api.get('/notifications/types').then(res => res.data.data),
    enabled: isOpen,
  });

  // Fetch organization staff for approval modal
  const { data: organizationStaff = [] } = useQuery({
    queryKey: ['organization-staff'],
    queryFn: () => api.get('/organizations/staff').then(res => res.data.data),
    enabled: isOpen,
  });

  // Mark as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: (notificationId: string) => 
      api.patch(`/notifications/${notificationId}/read`),
    onSuccess: () => {
      queryClient.invalidateQueries(['notifications']);
      queryClient.invalidateQueries(['notifications', 'unread-count']);
    },
  });

  // Mark as completed mutation
  const markAsCompletedMutation = useMutation({
    mutationFn: (notificationId: string) => 
      api.patch(`/notifications/${notificationId}/complete`),
    onSuccess: () => {
      queryClient.invalidateQueries(['notifications']);
      queryClient.invalidateQueries(['notifications', 'unread-count']);
    },
  });

  // Delete notification mutation
  const deleteNotificationMutation = useMutation({
    mutationFn: (notificationId: string) => 
      api.delete(`/notifications/${notificationId}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['notifications']);
      queryClient.invalidateQueries(['notifications', 'unread-count']);
      if (selectedNotification && deleteNotificationMutation.variables === selectedNotification.id) {
        setSelectedNotification(null);
      }
    },
  });

  // Approve join request mutation (updated to use staff data)
  const approveRequestMutation = useMutation({
    mutationFn: ({ requestId, staffData }: { requestId: string; staffData: any }) => {
      return api.post(`/organizations/join-requests/${requestId}/approve`, { staffData });
    },
    onSuccess: (response) => {
      console.log('✅ FRONTEND: Approval successful:', response);
      queryClient.invalidateQueries(['notifications']);
      queryClient.invalidateQueries(['notifications', 'unread-count']);
      queryClient.invalidateQueries(['organization-staff']);
      // Don't invalidate user data as it might cause OnboardingGuard to re-evaluate incorrectly
      setShowStaffApprovalModal(false);
      setCurrentJoinRequest(null);
    },
    onError: (error) => {
      console.error('💥 FRONTEND: Approval failed:', error);
      // Show user-friendly error message
      showAlert('Failed to approve join request. Please try again.');
    },
  });

  // Reject join request mutation
  const rejectRequestMutation = useMutation({
    mutationFn: ({ requestId, reason }: { requestId: string; reason?: string }) => 
      api.post(`/organizations/join-requests/${requestId}/reject`, { reason }),
    onSuccess: () => {
      queryClient.invalidateQueries(['notifications']);
      queryClient.invalidateQueries(['notifications', 'unread-count']);
      setShowStaffApprovalModal(false);
      setCurrentJoinRequest(null);
    },
  });

  const createMissingDocumentMutation = useMutation({
    mutationFn: (notificationId: string) =>
      api.post(`/agent/missing-document/${notificationId}/create`),
    onSuccess: (response) => {
      queryClient.invalidateQueries(['notifications']);
      queryClient.invalidateQueries(['notifications', 'unread-count']);
      queryClient.invalidateQueries(['page']);
      queryClient.invalidateQueries(['page-init']);
      queryClient.invalidateQueries(['tiles']);

      const linkUrl = response.data?.data?.linkUrl;
      if (linkUrl) {
        navigate(linkUrl);
        onClose();
      }
    },
    onError: () => {
      showAlert('Failed to create document. Please try again.');
    },
  });

  // Handle notification selection and auto-read/auto-complete
  const handleNotificationSelect = (notification: Notification) => {
    // Clear existing timer
    if (readTimer) {
      clearTimeout(readTimer);
      setReadTimer(null);
    }

    setSelectedNotification(notification);

    // Clear any previous error states when selecting a notification
    approveRequestMutation.reset();
    rejectRequestMutation.reset();

    // Set timer to mark as read/completed after 10 seconds (only if unread)
    if (notification.userStatus?.status === 'unread') {
      const timer = setTimeout(() => {
        // For content_approval_approved and content_approval_rejected notifications, mark as 'completed' instead of 'read'
        // (since reading the notification completes it - no separate call to action)
        if (notification.type === 'content_approval_approved' || notification.type === 'content_approval_rejected') {
          markAsCompletedMutation.mutate(notification.id);
        } else {
          // Check if notification has autoComplete flag in customData
          const customData = notification.customData as any;
          const shouldAutoComplete = customData?.autoComplete === true;
          
          if (shouldAutoComplete) {
            markAsCompletedMutation.mutate(notification.id);
          } else {
            markAsReadMutation.mutate(notification.id);
          }
        }
      }, 10000);
      setReadTimer(timer);
    }
  };

  // Handle notification deselection (also triggers read/completed if unread)
  const handleNotificationDeselect = () => {
    if (readTimer) {
      clearTimeout(readTimer);
      setReadTimer(null);
    }

    // Mark as read/completed immediately if currently selected notification is unread
    if (selectedNotification?.userStatus?.status === 'unread') {
      // For content_approval_approved and content_approval_rejected notifications, mark as 'completed' instead of 'read'
      // (since reading the notification completes it - no separate call to action)
      if (selectedNotification.type === 'content_approval_approved' || selectedNotification.type === 'content_approval_rejected') {
        markAsCompletedMutation.mutate(selectedNotification.id);
      } else {
        // Check if notification has autoComplete flag in customData
        const customData = selectedNotification.customData as any;
        const shouldAutoComplete = customData?.autoComplete === true;
        
        if (shouldAutoComplete) {
          markAsCompletedMutation.mutate(selectedNotification.id);
        } else {
          markAsReadMutation.mutate(selectedNotification.id);
        }
      }
    }

    setSelectedNotification(null);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (readTimer) {
        clearTimeout(readTimer);
      }
    };
  }, [readTimer]);

  // Clear error states when modal opens
  useEffect(() => {
    if (isOpen) {
      approveRequestMutation.reset();
      rejectRequestMutation.reset();
    }
  }, [isOpen]);

  // Sync selectedNotification with updated notifications data
  // This ensures the UI updates when notification status changes (e.g., completed, canDelete)
  useEffect(() => {
    if (selectedNotification && notifications.length > 0) {
      const updatedNotification = notifications.find((n: Notification) => n.id === selectedNotification.id);
      if (updatedNotification) {
        // Only update if the data has actually changed to avoid unnecessary re-renders
        if (
          updatedNotification.userStatus?.status !== selectedNotification.userStatus?.status ||
          updatedNotification.canDelete !== selectedNotification.canDelete
        ) {
          setSelectedNotification(updatedNotification);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifications]);

  // Auto-select notification when selectedNotificationId is provided
  useEffect(() => {
    if (selectedNotificationId && notifications.length > 0) {
      const notification = notifications.find((n: Notification) => n.id === selectedNotificationId);
      if (notification) {
        handleNotificationSelect(notification);
      }
    }
  }, [selectedNotificationId, notifications]);

  // Close modal and cleanup
  const handleClose = () => {
    handleNotificationDeselect();
    onClose();
  };

  // Get status icon
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'unread':
        return <Circle className="h-3 w-3 text-blue-500 fill-blue-500" />;
      case 'read':
        return <Circle className="h-3 w-3 text-gray-400" />;
      case 'completed':
        return <CheckCircle className="h-3 w-3 text-green-500" />;
      case 'cancelled':
        return <AlertCircle className="h-3 w-3 text-red-500" />;
      default:
        return <Circle className="h-3 w-3 text-gray-400" />;
    }
  };

  // Get assignment icon
  const getAssignmentIcon = (notification: Notification, isSelected: boolean = false) => {
    const iconColor = isSelected ? 'text-accent-foreground/70' : 'text-muted-foreground';
    
    if (notification.assignedToType === 'group') {
      const groupName = notification.assignedToId === 'org_admins' 
        ? 'Organization Admins' 
        : notification.assignedToId;
      return (
        <Users className={`h-3 w-3 ${iconColor}`} aria-label={`Assigned to ${groupName}`} />
      );
    } else {
      return (
        <User className={`h-3 w-3 ${iconColor}`} aria-label="Assigned to individual" />
      );
    }
  };

  const getNotificationDisplayTitle = (notification: Notification) =>
    notification.title || formatNotificationType(notification.type);

  const getNotificationListDetail = (notification: Notification) => {
    if (notification.type === 'workflow_step_task') {
      return notification.description || 'Workflow step ready for completion';
    }

    if (notification.type === 'form_stage_task') {
      return notification.description || 'Form submitted for your input';
    }

    if (notification.type === 'ai_missing_document') {
      return notification.customData?.documentTitle ||
        notification.customData?.suggestedTitle ||
        'Suggested document';
    }

    if (notification.customData?.firstName && notification.customData?.lastName) {
      return `${notification.customData.firstName} ${notification.customData.lastName}${notification.customData.jobTitle ? `, ${notification.customData.jobTitle}` : ''}`;
    }

    return '';
  };

  const withCurrentAccessContext = (targetUrl: string) => {
    const [path, queryString = ''] = targetUrl.split('?');
    const targetParams = new URLSearchParams(queryString);
    const currentParams = new URLSearchParams(location.search);

    ['impersonate', 'ghost', 'ghostSession', 'org'].forEach((key) => {
      const value = currentParams.get(key);
      if (value && !targetParams.has(key)) {
        targetParams.set(key, value);
      }
    });

    const nextQuery = targetParams.toString();
    return nextQuery ? `${path}?${nextQuery}` : path;
  };

  // Format notification type for display
  const formatNotificationType = (type: string) => {
    return type.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  // Helper function to safely extract join request data from customData
  const getJoinRequestData = (notification: Notification) => {
    const customData = notification.customData;
    if (!customData) {
      return null;
    }
    
    return {
      joinRequestId: customData.joinRequestId || '',
      firstName: customData.firstName || '',
      lastName: customData.lastName || '',
      preferredName: customData.preferredName || '',
      email: customData.email || '',
      jobTitle: customData.jobTitle || '',
      message: customData.message || ''
    };
  };

  const formatCustomDataValue = (value: unknown) => {
    if (Array.isArray(value)) {
      return value.filter(Boolean).join(' > ');
    }
    return typeof value === 'string' ? value : '';
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-background rounded-lg shadow-xl w-full max-w-6xl h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <Bell className="h-6 w-6 text-primary" />
            <h2 className="text-xl font-semibold text-foreground">My Tasks and Notifications</h2>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-accent rounded-md transition-colors text-muted-foreground hover:text-foreground"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-1 overflow-hidden">
          {/* Left Panel - Notifications List */}
          <div className="w-2/5 border-r border-border flex flex-col">
            {/* Filters and Sorting */}
            <div className="p-4 border-b border-border space-y-3">
              {/* Type Filter */}
              <select
                value={filters.type}
                onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
                className="w-full px-3 py-2 border border-input rounded-md text-sm bg-background text-foreground"
              >
                <option value="">All Types</option>
                {notificationTypes.map((type: string) => (
                  <option key={type} value={type}>
                    {formatNotificationType(type)}
                  </option>
                ))}
              </select>

              {/* Status Filter */}
              <select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                className="w-full px-3 py-2 border border-input rounded-md text-sm bg-background text-foreground"
              >
                <option value="">All Statuses</option>
                <option value="unread">Unread</option>
                <option value="read">Read</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>

              {/* Sort Options */}
              <div className="flex gap-2">
                <select
                  value={filters.sortBy}
                  onChange={(e) => setFilters(prev => ({ ...prev, sortBy: e.target.value as 'date' | 'type' }))}
                  className="flex-1 px-3 py-2 border border-input rounded-md text-sm bg-background text-foreground"
                >
                  <option value="date">Sort by Date</option>
                  <option value="type">Sort by Type</option>
                </select>
                <button
                  onClick={() => setFilters(prev => ({ 
                    ...prev, 
                    sortOrder: prev.sortOrder === 'asc' ? 'desc' : 'asc' 
                  }))}
                  className="px-3 py-2 border border-input rounded-md hover:bg-accent text-muted-foreground hover:text-foreground"
                  title={`Sort ${filters.sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
                >
                  <SortAsc className={`h-4 w-4 ${filters.sortOrder === 'desc' ? 'rotate-180' : ''}`} />
                </button>
              </div>
            </div>

            {/* Notifications List */}
            <div className="flex-1 overflow-y-auto">
              {isLoading ? (
                <div className="p-4 text-center text-muted-foreground">Loading notifications...</div>
              ) : notifications.length === 0 ? (
                <div className="p-4 text-center text-muted-foreground">No notifications found</div>
              ) : (
                notifications.map((notification: Notification) => (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationSelect(notification)}
                    className={`p-4 border-b border-border cursor-pointer hover:bg-accent/50 transition-colors ${
                      selectedNotification?.id === notification.id ? 'bg-accent border-primary' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Status Icon */}
                      <div className="mt-1">
                        {getStatusIcon(notification.userStatus?.status || 'unread')}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <h3 className={`text-sm truncate ${
                            selectedNotification?.id === notification.id ? 'text-accent-foreground' : 'text-foreground'
                          } ${
                            notification.userStatus?.status === 'unread' ? 'font-bold' : 'font-medium'
                          }`}>
                            {getNotificationDisplayTitle(notification)}
                          </h3>
                          <div className="flex items-center gap-1">
                            {getAssignmentIcon(notification, selectedNotification?.id === notification.id)}
                          </div>
                        </div>
                        
                        <p className={`text-xs mt-1 ${
                          selectedNotification?.id === notification.id ? 'text-accent-foreground/70' : 'text-muted-foreground'
                        }`}>
                          {new Date(notification.createdAt).toLocaleString()}
                        </p>

                        {/* Custom Data Display */}
                        {getNotificationListDetail(notification) && (
                          <p className={`text-xs mt-1 truncate ${
                            selectedNotification?.id === notification.id ? 'text-accent-foreground/70' : 'text-muted-foreground'
                          }`}>
                            {getNotificationListDetail(notification)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Right Panel - Notification Details */}
          <div className="flex-1 flex flex-col">
            {selectedNotification ? (
              <>
                {/* Details Header */}
                <div className="p-6 border-b border-border">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-foreground">
                      {getNotificationDisplayTitle(selectedNotification)}
                    </h3>
                    <div className="flex items-center gap-2">
                      {selectedNotification.canDelete && (
                        <button
                          onClick={() => deleteNotificationMutation.mutate(selectedNotification.id)}
                          disabled={deleteNotificationMutation.isLoading}
                          className="px-3 py-1 text-sm text-destructive border border-destructive rounded hover:bg-destructive/10 disabled:opacity-50"
                        >
                          Delete
                        </button>
                      )}
                      {/* Show "Mark as Read" button for unread notifications */}
                      {selectedNotification.userStatus?.status === 'unread' && (
                        <button
                          onClick={() => {
                            // For approval approved/rejected notifications, mark as completed (reading is the only action)
                            if (selectedNotification.type === 'content_approval_approved' || selectedNotification.type === 'content_approval_rejected') {
                              markAsCompletedMutation.mutate(selectedNotification.id);
                            } else {
                              // For other notifications, mark as read
                              markAsReadMutation.mutate(selectedNotification.id);
                            }
                          }}
                          disabled={markAsReadMutation.isLoading || markAsCompletedMutation.isLoading}
                          className="px-3 py-1 text-sm text-blue-600 border border-blue-300 rounded hover:bg-blue-50 disabled:opacity-50"
                        >
                          Mark as Read
                        </button>
                      )}
                      {/* Show "Mark as completed" button for content_approval_approved/content_approval_rejected when unread (handled by Mark as Read above), or for other notifications when read */}
                      {(selectedNotification.userStatus?.status === 'read' &&
                         selectedNotification.type !== 'approve_new_member' &&
                         !selectedNotification.canDelete &&
                         selectedNotification.type !== 'content_approval_approved' &&
                         selectedNotification.type !== 'content_approval_rejected') && (
                        <button
                          onClick={() => markAsCompletedMutation.mutate(selectedNotification.id)}
                          disabled={markAsCompletedMutation.isLoading}
                          className="px-3 py-1 text-sm text-green-600 border border-green-300 rounded hover:bg-green-50 disabled:opacity-50"
                        >
                          Mark Complete
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    {getStatusIcon(selectedNotification.userStatus?.status || 'unread')}
                    <span className="text-sm text-muted-foreground capitalize">
                      {selectedNotification.userStatus?.status || 'unread'}
                    </span>
                    <span className="text-sm text-muted-foreground">•</span>
                    <span className="text-sm text-muted-foreground">
                      {new Date(selectedNotification.createdAt).toLocaleString()}
                    </span>
                  </div>
                </div>

                {/* Details Content */}
                <div className="flex-1 p-6 overflow-y-auto">
                  <div className="prose max-w-none">
                    <p className="text-foreground whitespace-pre-wrap">
                      {selectedNotification.description}
                    </p>

                    {/* Content Approval Request/Approved/Rejected Details */}
                    {(selectedNotification.type === 'content_approval_request' || selectedNotification.type === 'content_approval_approved' || selectedNotification.type === 'content_approval_rejected') && selectedNotification.customData && (
                      <div className="mt-6 p-4 bg-muted rounded-lg">
                        <h4 className="font-medium text-foreground mb-3">Approval Details</h4>
                        <div className="space-y-2 text-sm">
                          {selectedNotification.customData.tileTitle && (
                            <div>
                              <span className="font-medium text-foreground">Content name:</span>{' '}
                              <span className="text-foreground">{selectedNotification.customData.tileTitle}</span>
                            </div>
                          )}
                          {selectedNotification.customData.pageTitle && (
                            <div>
                              <span className="font-medium text-foreground">Parent Page:</span>{' '}
                              <span className="text-foreground">{selectedNotification.customData.pageTitle}</span>
                            </div>
                          )}
                          {selectedNotification.customData.requestorName && selectedNotification.type === 'content_approval_request' && (
                            <div>
                              <span className="font-medium text-foreground">Requestor:</span>{' '}
                              <span className="text-foreground">{selectedNotification.customData.requestorName}</span>
                            </div>
                          )}
                          {/* Link for approved/rejected notifications - display as button */}
                          {(selectedNotification.type === 'content_approval_approved' || selectedNotification.type === 'content_approval_rejected') && selectedNotification.customData.linkUrl && (
                            <div className="mt-4">
                              <button
                                onClick={() => {
                                  const linkUrl = selectedNotification.customData.linkUrl;
                                  
                                  // Parse the URL to extract path and query params
                                  const [path, queryString] = linkUrl.split('?');
                                  const urlParams = queryString ? new URLSearchParams(queryString) : new URLSearchParams();
                                  const highlightTileId = urlParams.get('highlight');
                                  
                                  // Check if we're already on the target page
                                  if (location.pathname === path && highlightTileId) {
                                    // Re-trigger highlight by removing and re-adding the parameter
                                    const currentParams = new URLSearchParams(searchParams);
                                    currentParams.delete('highlight');
                                    setSearchParams(currentParams, { replace: true });
                                    
                                    // Re-add highlight immediately to trigger the effect
                                    setTimeout(() => {
                                      const updatedParams = new URLSearchParams(window.location.search);
                                      updatedParams.set('highlight', highlightTileId);
                                      // Preserve all other existing params
                                      Array.from(searchParams.entries()).forEach(([key, value]) => {
                                        if (key !== 'highlight') {
                                          updatedParams.set(key, value);
                                        }
                                      });
                                      setSearchParams(updatedParams, { replace: true });
                                    }, 0);
                                  } else {
                                    // Navigate to different page
                                    navigate(linkUrl);
                                  }
                                  onClose();
                                }}
                                className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90 transition-colors"
                              >
                                View Item
                              </button>
                            </div>
                          )}
                        </div>
                        
                        {/* Link button - only show for approval requests (not approved notifications) */}
                        {selectedNotification.type === 'content_approval_request' && 
                         selectedNotification.customData.linkUrl && 
                         selectedNotification.userStatus?.status !== 'completed' && (
                          <div className="mt-4">
                            <button
                              onClick={() => {
                                const linkUrl = selectedNotification.customData.linkUrl;
                                
                                // Parse the URL to extract path and query params
                                const [path, queryString] = linkUrl.split('?');
                                const urlParams = queryString ? new URLSearchParams(queryString) : new URLSearchParams();
                                const highlightTileId = urlParams.get('highlight');
                                
                                // Check if we're already on the target page
                                if (location.pathname === path && highlightTileId) {
                                  // Re-trigger highlight by removing and re-adding the parameter
                                  // This ensures the useEffect in CustomizableGrid runs again
                                  const currentParams = new URLSearchParams(searchParams);
                                  currentParams.delete('highlight');
                                  setSearchParams(currentParams, { replace: true });
                                  
                                  // Re-add highlight immediately to trigger the effect
                                  setTimeout(() => {
                                    const updatedParams = new URLSearchParams(window.location.search);
                                    updatedParams.set('highlight', highlightTileId);
                                    // Preserve all other existing params
                                    Array.from(searchParams.entries()).forEach(([key, value]) => {
                                      if (key !== 'highlight') {
                                        updatedParams.set(key, value);
                                      }
                                    });
                                    setSearchParams(updatedParams, { replace: true });
                                  }, 0);
                                } else {
                                  // Navigate to different page
                                  navigate(linkUrl);
                                }
                                onClose();
                              }}
                              className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90 transition-colors"
                            >
                              View Item
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {selectedNotification.type === 'ai_missing_document' && selectedNotification.customData && (
                      <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex items-start gap-3">
                          <FilePlus2 className="h-5 w-5 text-amber-700 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <h4 className="font-medium text-amber-950 mb-3">
                              Suggested {selectedNotification.customData.documentType === 'form' ? 'form' : 'document'}
                            </h4>
                            <div className="space-y-2 text-sm">
                              {(selectedNotification.customData.documentTitle || selectedNotification.customData.suggestedTitle) && (
                                <div>
                                  <span className="font-medium text-amber-950">Title:</span>{' '}
                                  <span className="text-amber-950">
                                    {selectedNotification.customData.documentTitle || selectedNotification.customData.suggestedTitle}
                                  </span>
                                </div>
                              )}
                              {selectedNotification.customData.documentType && (
                                <div>
                                  <span className="font-medium text-amber-950">Type:</span>{' '}
                                  <span className="text-amber-950">{selectedNotification.customData.documentType}</span>
                                </div>
                              )}
                              {selectedNotification.customData.suggestedPagePath && (
                                <div>
                                  <span className="font-medium text-amber-950">Suggested location:</span>{' '}
                                  <span className="text-amber-950">
                                    {formatCustomDataValue(selectedNotification.customData.suggestedPagePath)}
                                  </span>
                                </div>
                              )}
                              {(selectedNotification.customData.sourceTitle || selectedNotification.customData.sourceDocumentTitle) && (
                                <div>
                                  <span className="font-medium text-amber-950">Identified while editing:</span>{' '}
                                  <span className="text-amber-950">
                                    {selectedNotification.customData.sourceTitle || selectedNotification.customData.sourceDocumentTitle}
                                  </span>
                                </div>
                              )}
                              {selectedNotification.customData.triggerText && (
                                <div>
                                  <span className="font-medium text-amber-950">Reference that needs support:</span>
                                  <p className="mt-1 text-amber-900 whitespace-pre-wrap">{selectedNotification.customData.triggerText}</p>
                                </div>
                              )}
                              {selectedNotification.customData.reason && (
                                <div>
                                  <span className="font-medium text-amber-950">Why this is needed:</span>
                                  <p className="mt-1 text-amber-900 whitespace-pre-wrap">{selectedNotification.customData.reason}</p>
                                </div>
                              )}
                            </div>

                            {selectedNotification.userStatus?.status !== 'completed' &&
                              selectedNotification.userStatus?.status !== 'cancelled' && (
                              <div className="mt-4 flex flex-wrap gap-2">
                                <button
                                  onClick={() => createMissingDocumentMutation.mutate(selectedNotification.id)}
                                  disabled={createMissingDocumentMutation.isLoading}
                                  className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                  {createMissingDocumentMutation.isLoading
                                    ? 'Creating...'
                                    : selectedNotification.customData.documentType === 'form'
                                      ? 'Create form'
                                      : 'Create document'}
                                </button>
                              </div>
                            )}

                            {createMissingDocumentMutation.isError && (
                              <div className="mt-4 p-3 bg-red-100 border border-red-300 rounded text-red-800">
                                Error creating document. Please try again.
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {selectedNotification.type === 'form_stage_task' && selectedNotification.customData?.formId && selectedNotification.customData?.submissionId && (
                      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <h4 className="font-medium text-blue-950 mb-3">Form Task</h4>
                        <div className="space-y-2 text-sm text-blue-950">
                          {selectedNotification.customData.actionType && (
                            <div>
                              <span className="font-medium">Action:</span>{' '}
                              <span>{formatCustomDataValue(String(selectedNotification.customData.actionType))}</span>
                            </div>
                          )}
                          {selectedNotification.customData.stageCompletionId && (
                            <div>
                              <span className="font-medium">Stage:</span>{' '}
                              <span>{String(selectedNotification.customData.stageCompletionId)}</span>
                            </div>
                          )}
                          {selectedNotification.customData.returnMessage && (
                            <div>
                              <span className="font-medium">Return reason:</span>
                              <p className="mt-1 whitespace-pre-wrap">{String(selectedNotification.customData.returnMessage)}</p>
                            </div>
                          )}
                        </div>
                        <div className="mt-4">
                          <button
                            onClick={() => {
                              const baseLink = selectedNotification.customData?.linkUrl ||
                                `/forms?formId=${selectedNotification.customData?.formId}&submissionId=${selectedNotification.customData?.submissionId}`;
                              const separator = baseLink.includes('?') ? '&' : '?';
                              navigate(withCurrentAccessContext(`${baseLink}${separator}notificationId=${selectedNotification.id}&taskOpen=${Date.now()}`));
                              onClose();
                            }}
                            className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90 transition-colors"
                          >
                            Open Form Task
                          </button>
                        </div>
                      </div>
                    )}

                    {selectedNotification.type === 'workflow_step_task' && selectedNotification.customData?.url && (
                      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <h4 className="font-medium text-blue-950 mb-3">Workflow Task</h4>
                        <div className="space-y-2 text-sm text-blue-950">
                          {selectedNotification.customData.workflowRecordId && (
                            <div>
                              <span className="font-medium">Record:</span>{' '}
                              <span>{String(selectedNotification.customData.workflowRecordId)}</span>
                            </div>
                          )}
                          {selectedNotification.customData.stepId && (
                            <div>
                              <span className="font-medium">Step:</span>{' '}
                              <span>{String(selectedNotification.customData.stepId)}</span>
                            </div>
                          )}
                        </div>
                        <div className="mt-4">
                          <button
                            onClick={() => {
                              navigate(withCurrentAccessContext(String(selectedNotification.customData?.url)));
                              onClose();
                            }}
                            className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90 transition-colors"
                          >
                            Open Workflow Task
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Custom notification content will be added here based on type */}
                    {selectedNotification.type === 'approve_new_member' && selectedNotification.customData && (
                      <div className="mt-6 p-4 bg-muted rounded-lg">
                        <h4 className="font-medium text-foreground mb-3">User Details</h4>
                        <div className="space-y-2 text-sm">
                          {(() => {
                            const joinRequestData = getJoinRequestData(selectedNotification);
                            if (!joinRequestData) return null;
                            
                            return (
                              <>
                                <div>
                                  <span className="font-medium text-foreground">Name:</span> <span className="text-foreground">{joinRequestData.firstName} {joinRequestData.lastName}</span>
                                </div>
                                {joinRequestData.preferredName && (
                                  <div>
                                    <span className="font-medium text-foreground">Preferred Name:</span> <span className="text-foreground">{joinRequestData.preferredName}</span>
                                  </div>
                                )}
                                <div>
                                  <span className="font-medium text-foreground">Email:</span> <span className="text-foreground">{joinRequestData.email}</span>
                                </div>
                                {joinRequestData.jobTitle && (
                                  <div>
                                    <span className="font-medium text-foreground">Job Title:</span> <span className="text-foreground">{joinRequestData.jobTitle}</span>
                                  </div>
                                )}
                                {joinRequestData.message && (
                                  <div>
                                    <span className="font-medium text-foreground">Message:</span>
                                    <p className="mt-1 text-muted-foreground">{joinRequestData.message}</p>
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </div>

                        {/* Approve/Reject buttons */}
                        {selectedNotification.userStatus?.status !== 'completed' && (
                          <div className="mt-4 flex gap-2">
                            <button 
                              onClick={() => {
                                const joinRequestData = getJoinRequestData(selectedNotification);
                                if (joinRequestData) {
                                  setCurrentJoinRequest({
                                    id: joinRequestData.joinRequestId,
                                    firstName: joinRequestData.firstName,
                                    lastName: joinRequestData.lastName,
                                    preferredName: joinRequestData.preferredName,
                                    email: joinRequestData.email,
                                    jobTitle: joinRequestData.jobTitle,
                                    message: joinRequestData.message
                                  });
                                  setShowStaffApprovalModal(true);
                                }
                              }}
                              disabled={approveRequestMutation.isLoading || rejectRequestMutation.isLoading}
                              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Approve User
                            </button>
                            <button 
                              onClick={() => {
                                const joinRequestData = getJoinRequestData(selectedNotification);
                                if (joinRequestData) {
                                  setCurrentJoinRequest({
                                    id: joinRequestData.joinRequestId,
                                    firstName: joinRequestData.firstName,
                                    lastName: joinRequestData.lastName,
                                    preferredName: joinRequestData.preferredName,
                                    email: joinRequestData.email,
                                    jobTitle: joinRequestData.jobTitle,
                                    message: joinRequestData.message
                                  });
                                  setShowStaffApprovalModal(true);
                                }
                              }}
                              disabled={approveRequestMutation.isLoading || rejectRequestMutation.isLoading}
                              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Reject User
                            </button>
                          </div>
                        )}

                        {/* Show success/error messages */}
                        {approveRequestMutation.isSuccess && (
                          <div className="mt-4 p-3 bg-green-100 border border-green-300 rounded text-green-800">
                            ✅ User approved successfully! They now have access to the organization.
                          </div>
                        )}
                        {rejectRequestMutation.isSuccess && (
                          <div className="mt-4 p-3 bg-red-100 border border-red-300 rounded text-red-800">
                            ❌ User request rejected.
                          </div>
                        )}
                        {(approveRequestMutation.isError || rejectRequestMutation.isError) && (
                          <div className="mt-4 p-3 bg-red-100 border border-red-300 rounded text-red-800">
                            Error processing request. Please try again.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <Bell className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                  <p>Select a notification to view details</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Staff Approval Modal */}
      <StaffApprovalModal
        isOpen={showStaffApprovalModal}
        onClose={() => {
          setShowStaffApprovalModal(false);
          setCurrentJoinRequest(null);
        }}
        onApprove={(staffData) => {
          if (currentJoinRequest) {
            approveRequestMutation.mutate({
              requestId: currentJoinRequest.id,
              staffData
            });
          }
        }}
        onReject={(reason) => {
          if (currentJoinRequest) {
            rejectRequestMutation.mutate({
              requestId: currentJoinRequest.id,
              reason
            });
          }
        }}
        joinRequest={currentJoinRequest}
        existingStaff={organizationStaff}
        isLoading={approveRequestMutation.isLoading || rejectRequestMutation.isLoading}
      />
    </div>,
    document.body
  );
}
